import React, { useEffect, useState } from 'react'
import PageConfig from '@/component/layout/PageConfig.tsx'
import SearchBar from '@/component/input/SearchBar.tsx'
import SearchUserItem from '@/pages/users/SearchUserItem.tsx'
import useQueryGetUsersStatusList from '@/api/user/getUsersStatusList.ts'
import useDebounce from '@/util/hook/useDebounce.tsx'
import UserDetail from '@/pages/users/UserDetail.tsx'
import { useSearchParams } from 'react-router-dom'
import { isEmptyArray, isNotEmptyArray } from '@/util/array.ts'
import Pagination from '@/component/pagination/Pagination.tsx'
import { useTranslation } from 'react-i18next'
import ArrowLeft from '@/assets/ic_arrow_left.svg?react'
import ArrowRight from '@/assets/ic_arrow_right.svg?react'

type IProps = {}

const PAGE_SIZE = 10

const Users: React.FC<IProps> = ({}) => {
    const { t } = useTranslation()

    const [searchParams, setSearchParams] = useSearchParams()

    const [search, setSearch] = useState('')
    const [userId, setUserId] = useState(Number(searchParams.get('id')))
    const [page, setPage] = useState(1) // 페이지는 1부터 시작
    const [isListOnlyMode, setIsListOnlyMode] = useState(false)
    const [isUserListCollapsed, setIsUserListCollapsed] = useState(false)

    const debouncedSearchText = useDebounce(search, 500) // 검색어 디바운스 시간 증가

    const { data, refetch, isLoading } = useQueryGetUsersStatusList(
        {
            pageNum: page,
            pageSize: PAGE_SIZE
        },
        debouncedSearchText
    )

    const users = data?.data || []
    const pagination = data?.pagination

    // 첫 번째 사용자를 자동 선택 (userId가 설정되지 않았을 때)
    useEffect(() => {
        if (!isListOnlyMode && userId <= 0 && users?.length > 0) {
            setUserId(users[0].id)
        }
    }, [data, userId, isListOnlyMode])

    // 검색어가 변경되면 첫 페이지로 이동
    useEffect(() => {
        setPage(1)
    }, [debouncedSearchText])

    const onRefresh = () => {
        setIsListOnlyMode(false)
        setUserId(-1)
        refetch()
    }

    const handlePageChange = (newPage: number) => {
        setPage(newPage)
    }

    const hasSelectedUser = userId > 0

    useEffect(() => {
        setSearchParams({ id: userId?.toString() })
    }, [userId])

    return (
        <>
            <PageConfig showTopNav={true} showSideNav={true}>
                {isLoading ? (
                    <div className="member-workspace__loading">
                        <span />
                        <strong>{t('users.workspace.loading')}</strong>
                    </div>
                ) : (
                    <div
                        className="member-workspace"
                        style={{
                            gridTemplateColumns: isUserListCollapsed
                                ? '52px minmax(0, 1fr)'
                                : '360px minmax(0, 1fr)'
                        }}
                    >
                        <aside className={`member-directory ${hasSelectedUser ? 'md:hidden' : ''}`}>
                            {isUserListCollapsed && (
                                <button
                                    type={'button'}
                                    aria-label={t('users.workspace.expandList')}
                                    aria-expanded={false}
                                    title={t('users.workspace.expandList')}
                                    className="member-directory__expand"
                                    onClick={() => setIsUserListCollapsed(false)}
                                >
                                    <ArrowRight className={'h-[16px] w-[16px]'} />
                                </button>
                            )}
                            <div className={`member-directory__viewport ${isUserListCollapsed ? 'h-full' : ''}`}>
                                <div
                                    className={`member-directory__content ${isUserListCollapsed ? 'pointer-events-none opacity-0' : 'opacity-100'}`}
                                >
                                    <div className="member-directory__heading">
                                        <div>
                                            <p>CAREARLY MEMBERS</p>
                                            <h1>{t('users.workspace.title')}</h1>
                                        </div>
                                        <span>
                                            <strong>{pagination?.totalEntries || users.length}</strong>{t('account.protectee.count')}
                                        </span>
                                    </div>
                                    <p className="member-directory__description">{t('users.workspace.description')}</p>
                                    <div className="member-directory__search">
                                        <SearchBar
                                            className="min-w-0 flex-1"
                                            inputParams={{
                                                placeholder: t('users.list.search.placeholder')
                                            }}
                                            onChanged={(text) => setSearch(text)}
                                        />
                                        <button
                                            type={'button'}
                                            aria-label={t('users.workspace.collapseList')}
                                            aria-expanded={true}
                                            title={t('users.workspace.collapseList')}
                                            className="member-directory__collapse"
                                            onClick={() => setIsUserListCollapsed(true)}
                                        >
                                            <ArrowLeft className={'h-[16px] w-[16px]'} />
                                        </button>
                                    </div>
                                    {isEmptyArray(users) && (
                                        <div className="member-directory__empty">
                                            <span>⌕</span>
                                            <strong>
                                                {debouncedSearchText
                                                    ? t('users.list.empty.search')
                                                    : t('users.list.empty')}
                                            </strong>
                                            <p>{t('users.workspace.emptyHint')}</p>
                                        </div>
                                    )}
                                    {isNotEmptyArray(users) && (
                                        <div className="member-directory__results">
                                            <div className="member-directory__result-meta">
                                                <span>{t('users.workspace.listTitle')}</span>
                                                <small>{t('users.workspace.pageOf', { page, total: pagination?.totalPages || 1 })}</small>
                                            </div>
                                            <div className="member-directory__list">
                                                {users?.map((item) => (
                                                    <SearchUserItem
                                                        {...item}
                                                        key={item?.id}
                                                        isSelected={item.id === userId}
                                                        onClick={(id: number) => {
                                                            setIsListOnlyMode(false)
                                                            setUserId(id)
                                                        }}
                                                    />
                                                ))}
                                            </div>
                                            {pagination && pagination.totalPages > 1 && (
                                                <Pagination
                                                    className="member-directory__pagination"
                                                    page_size={pagination.pageSize}
                                                    current_page={pagination.currentPage}
                                                    total_page={pagination.totalPages}
                                                    fetchPage={handlePageChange}
                                                />
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </aside>
                        {hasSelectedUser && (
                            <section className="member-workspace__detail">
                                <div className="member-workspace__mobile-back">
                                    <button
                                        className="member-workspace__back-button"
                                        onClick={() => {
                                            setIsListOnlyMode(true)
                                            setUserId(-1)
                                        }}
                                    >
                                        {t('mentalHealth.reportDetail.backToList')}
                                    </button>
                                </div>
                                <UserDetail
                                    className={''}
                                    id={userId}
                                    onRefresh={onRefresh}
                                />
                            </section>
                        )}
                        {!hasSelectedUser && (
                            <div className="member-workspace__empty-detail">
                                <span>👤</span>
                                <strong>{t('users.workspace.selectPrompt')}</strong>
                                <p>{t('users.list.empty')}</p>
                            </div>
                        )}
                    </div>
                )}
            </PageConfig>
        </>
    )
}

export default Users
